<template>
  <div>
    <v-row v-if="isLoading" class="d-flex justify-center mb-2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="50">
      </v-progress-circular>
    </v-row>
    <template v-else-if="evaluations.length">
      <div class="d-flex align-center mb-4" style="gap: 8px;">
        <v-icon small color="orange darken-2">mdi-alert-circle-outline</v-icon>
        <span class="caption grey--text text--darken-1">
          Rows highlighted in orange differ between the selected evaluations.
        </span>
      </div>
      <div class="compare-scroll">
        <table class="compare-table" :style="{ minWidth: 180 + evaluations.length * 220 + 'px' }">
          <thead>
            <tr>
              <th class="attr-col"></th>
              <th
                v-for="(e, i) in evaluations"
                :key="'head_' + i"
                class="eval-col">
                <div class="font-weight-medium">{{ e.flowType }}</div>
                <div v-if="e.updated_by" class="caption grey--text text--darken-1">
                  {{ e.updated_by }}
                  <div>{{ e.updated_date }}</div>
                </div>
                <v-chip
                  x-small
                  label
                  :color="evalStatusColour(e.status)"
                  text-color="white"
                  class="mt-1">
                  {{ e.status }}
                </v-chip>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="section in sections">
              <template v-if="section.rows.length">
                <tr :key="section.key + '_hdr'" class="section-row">
                  <td :colspan="evaluations.length + 1">{{ section.title }}</td>
                </tr>
                <tr
                  v-for="row in section.rows"
                  :key="section.key + '_' + row.id"
                  :class="{ 'diff-row': highlightChanges && row.differs }">
                  <td class="attr-col">{{ row.label }}</td>
                  <td
                    v-for="(cell, i) in row.cells"
                    :key="section.key + '_' + row.id + '_' + i"
                    class="eval-col">
                    <template v-if="cell">
                      <div class="d-flex align-start" style="gap: 4px;">
                        <span class="flex-grow-1">{{ cell.display }}</span>
                        <v-btn
                          v-if="cell.comment"
                          icon
                          x-small
                          @click="toggleComment(cellKey(section.key, row.id, i))">
                          <v-icon x-small>{{ isCommentExpanded(cellKey(section.key, row.id, i)) ? 'mdi-comment-remove-outline' : 'mdi-comment-text-outline' }}</v-icon>
                        </v-btn>
                      </div>
                      <div
                        v-if="cell.comment && isCommentExpanded(cellKey(section.key, row.id, i))"
                        class="caption grey--text text--darken-1 comment-cell">
                        {{ cell.comment }}
                      </div>
                    </template>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </template>
    <ResponseHandler :serviceResponse="response"></ResponseHandler>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import ResponseHandler from "@/components/ResponseHandler"; // @ is an alias to /src

export default {
  name: "DocumentLevellingEvaluationCompare",
  components: {
    ResponseHandler,
  },
  props: {
    doc_id: { type: Number, required: true },
    ids: { type: Array, required: true },
    highlightChanges: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const isLoading = ref(false);
    const response = ref(null);
    const evaluations = ref([]);
    const differences = ref({});
    const expandedComments = ref([]);

    // Comments/justifications are hidden by default; a per-cell toggle reveals them.
    const cellKey = (sectionKey, rowId, colIndex) => `${sectionKey}_${rowId}_${colIndex}`;
    const isCommentExpanded = (key) => expandedComments.value.includes(key);
    const toggleComment = (key) => {
      const i = expandedComments.value.indexOf(key);
      if (i === -1) expandedComments.value.push(key);
      else expandedComments.value.splice(i, 1);
    };

    const evalStatusColour = (status) => {
      switch ((status || "").toUpperCase()) {
        case "DRAFT": return "orange";
        case "COMPLETED": return "green";
        case "ARCHIVED": return "grey";
        default: return "blue";
      }
    };

    const formatValue = (value) =>
      value === null || value === undefined || value === "" ? "—" : value;

    // Summary (scalar) fields shown as one row each. A row is highlighted when the
    // backend flags that field as differing across the selected evaluations.
    const summaryFields = [
      { key: "score", label: "Score" },
      { key: "functionName", label: "Function" },
      { key: "trackName", label: "Track" },
      { key: "suggestedLevelAbbr", label: "Suggested Level" },
      { key: "selectedLevelAbbr", label: "Selected Level" },
      { key: "comments", label: "Comments" },
    ];

    const summaryRows = computed(() =>
      summaryFields.map((f) => ({
        id: f.key,
        label: f.label,
        differs: !!differences.value?.fields?.[f.key],
        cells: evaluations.value.map((e) => ({ display: formatValue(e[f.key]), comment: null })),
      }))
    );

    // Build one row per distinct answer (aligned across evaluations by its id), using
    // only active answers to match the current state. `differs` comes from the backend
    // comparison; `cellFn` turns an answer into the { display, comment } shown per column.
    const buildAnswerRows = (arrayKey, idKey, labelKey, cellFn) => {
      const order = [];
      const labels = {};
      evaluations.value.forEach((e) => {
        (e[arrayKey] || []).filter((a) => a.active).forEach((a) => {
          const id = a[idKey];
          if (!(id in labels)) {
            labels[id] = a[labelKey];
            order.push(id);
          }
        });
      });
      return order.map((id) => ({
        id,
        label: labels[id],
        differs: !!differences.value?.[arrayKey]?.[id]?.differs,
        cells: evaluations.value.map((e) => {
          const a = (e[arrayKey] || []).find((x) => x.active && x[idKey] === id);
          return a ? cellFn(a) : null;
        }),
      }));
    };

    const factorRows = computed(() =>
      buildAnswerRows("classificationAnswers", "factorId", "factorName", (a) => ({
        display: a.selectedLevel,
        comment: a.comments,
      }))
    );

    const subFactorRows = computed(() =>
      buildAnswerRows("standardAnswers", "subFactorId", "subFactorName", (a) => ({
        display: `${Math.round(a.selectedLevelScore)} — ${a.selectedLevelName}`,
        comment: a.comments,
      }))
    );

    const guidedRows = computed(() =>
      buildAnswerRows("guidedAnswers", "questionId", "questionId", (a) => ({
        display: a.selectedKey,
        comment: a.comments,
      }))
    );

    const sections = computed(() => [
      { key: "summary", title: "Summary", rows: summaryRows.value },
      { key: "factors", title: "Factors", rows: factorRows.value },
      { key: "subFactors", title: "Sub-Factors", rows: subFactorRows.value },
      { key: "guided", title: "Guided Answers", rows: guidedRows.value },
    ]);

    const getData = () => {
      isLoading.value = true;
      axios
        .post("document/getDocumentLfComparison", { ids: props.ids })
        .then((resp) => {
          if (resp.data.Status === "OK") {
            evaluations.value = resp.data.Data.evaluations || [];
            differences.value = resp.data.Data.differences || {};
          } else {
            alert(resp.data.Message);
          }
          isLoading.value = false;
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            console.log(err);
            response.value = err.response ? err.response.data : { message: "Unexpected Error" };
          }
          isLoading.value = false;
        });
    };

    onMounted(() => {
      getData();
    });

    return { isLoading, response, evaluations, sections, getData, evalStatusColour, cellKey, isCommentExpanded, toggleComment };
  },
};
</script>
<style scoped lang="scss">
.compare-scroll {
  overflow-x: auto;
}

.compare-table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  font-size: 13px;

  th,
  td {
    border: 1px solid #e0e0e0;
    padding: 8px 12px;
    text-align: left;
    vertical-align: top;
    word-break: break-word;
  }
}

.attr-col {
  width: 250px;
  font-weight: 600;
  color: #555;
  background: #fafafa;
  position: sticky;
  left: 0;
  z-index: 1;
}

// With table-layout: fixed and no explicit width, the evaluation columns
// share the remaining space equally, so every evaluation gets equal width.
.eval-col {
  width: auto;
}

.section-row td {
  background: #eef7f9;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: #37474f;
}

.diff-row {
  td {
    background: #fff6e5;
  }
  .attr-col {
    background: #ffe9c2;
  }
}

.comment-cell {
  white-space: pre-wrap;
  line-height: 1.4;
  margin-top: 2px;
}
</style>
